import { Clock, CheckCircle, XCircle } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Card } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { useSelector } from "react-redux";
import { getFeedbackForm } from "../../../redux/reducer/incbotFeedbackReducer";
import { formatDuration } from "../../../utils/helper";
import { MCQ_SECTION_NAMES } from "../../../constants";

export const AptitudeSection = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const sectionsData = feedbackForm?.aptitudeSectionData?.sectionsData || [];

  return (
    <>
      {/* Header with background */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm p-6 mb-6 border border-blue-100">
        <h1 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-2">MCQ Questions</h1>
        <p className="text-gray-600 text-sm lg:text-base">
          Insights into the candidate's analytical thinking and reasoning skills.
        </p>
      </div>

      {/* Map through each section */}
      {sectionsData.map((section: any, sectionIndex: number) => (
        <div key={sectionIndex} className={sectionIndex < sectionsData.length - 1 ? "mb-8" : ""}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 mb-6">
            <h2 className="text-md lg:text-xl font-medium text-gray-900">
              {MCQ_SECTION_NAMES[section.questionType] ?? section.questionType}
            </h2>
            <div className="flex flex-wrap items-center gap-2 lg:gap-3">
              <Badge
                variant="outline"
                className="bg-cyan-50 text-cyan-700 border-cyan-200 text-xs lg:text-sm"
              >
                📊 Marks Scored: {section.totalMarks} out of {section.responses.length || 0}
              </Badge>
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 text-xs lg:text-sm"
              >
                🎯 Difficulty Level: {section.difficultyLevel}
              </Badge>
              <div className="flex items-center gap-1 text-gray-600">
                <Clock size={14} className="lg:hidden" />
                <Clock size={16} className="hidden lg:block" />
                <span className="text-xs lg:text-sm">
                  {formatDuration(section.lastQuestionTime)}
                </span>
              </div>
            </div>
          </div>

          {/* Questions */}
          {section.responses.map((question: any, questionIndex: number) => (
            <Card
              key={questionIndex}
              className={
                questionIndex < section.responses.length - 1
                  ? "rounded-lg shadow-sm mb-4"
                  : "rounded-lg shadow-sm"
              }
            >
              <div className="bg-white rounded-lg p-4 lg:p-6">
                <div className="flex items-start gap-3 lg:gap-4">
                  <span className="text-lg font-medium text-gray-700 mt-1 flex-shrink-0">
                    {questionIndex + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 gap-3">
                      <div className="flex-1 lg:pr-4">
                        <p className="text-gray-900 mb-4 text-sm lg:text-base leading-relaxed break-words whitespace-pre-wrap">
                          {question.question}
                        </p>
                        {(question.option_1 ||
                          question.option_2 ||
                          question.option_3 ||
                          question.option_4) && (
                          <div
                            className="space-y-2 text-gray-700 text-sm lg:text-base break-words whitespace-pre-wrap"
                            style={{ wordBreak: "break-word" }}
                          >
                            {question.option_1 && (
                              <div className="break-words whitespace-pre-wrap">
                                {question.option_1}
                              </div>
                            )}
                            {question.option_2 && (
                              <div className="break-words whitespace-pre-wrap">
                                {question.option_2}
                              </div>
                            )}
                            {question.option_3 && (
                              <div className="break-words whitespace-pre-wrap">
                                {question.option_3}
                              </div>
                            )}
                            {question.option_4 && (
                              <div className="break-words whitespace-pre-wrap">
                                {question.option_4}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {question.rating > 0 ? (
                          <>
                            <CheckCircle className="text-green-500" size={18} />
                            <Badge
                              variant="outline"
                              className="text-green-700 border-green-200 bg-green-50 text-xs lg:text-sm"
                            >
                              Correct
                            </Badge>
                          </>
                        ) : (
                          <>
                            <XCircle className="text-red-500" size={18} />
                            <Badge
                              variant="outline"
                              className="text-red-700 border-red-200 bg-red-50 text-xs lg:text-sm"
                            >
                              Incorrect
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-2 block">
                          User Input:
                        </Label>
                        <Input
                          value={question.answer || "No answer provided"}
                          readOnly
                          className={
                            question.rating > 0
                              ? "bg-green-50 border-green-200 text-sm"
                              : "bg-red-50 border-red-200 text-sm"
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-2 block">
                          Correct Answer:
                        </Label>
                        <Input
                          value={question.predefinedAnswer || "Not available"}
                          readOnly
                          className="bg-blue-50 border-blue-200 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ))}
    </>
  );
};
